import { useQuery, useQueryClient, useMutation } from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	CustomTaxReliefGroupDto,
	CustomTaxReliefGroupPaginator,
	CreateCustomTaxReliefGroupDto,
	QueryCustomTaxReliefGroupDto,
	SearchCustomTaxReliefGroupDto,
	UpdateCustomTaxReliefGroupDto,
} from "../models/custom-tax-relief-group.dto";
import ENDPOINTS from "./utils/endpoints";

export function useGetCustomTaxReliefGroup(
	companyId: number,
	options?: QueryCustomTaxReliefGroupDto,
) {
	const url = ENDPOINTS.PROCESSING_COMPANY.CUSTOMTAXRELIEFGROUP.GET;
	const request = (options: QueryCustomTaxReliefGroupDto) =>
		API.ProcessingCompanyAPI.getCustomTaxReliefGroups(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		CustomTaxReliefGroupPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetCustomTaxReliefGroupSearch(companyId: number) {
	return useMutation(
		(options?: SearchCustomTaxReliefGroupDto) =>
			API.ProcessingCompanyAPI.getCustomTaxReliefGroupSearch(
				companyId,
				options,
			),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetCustomTaxReliefGroupById(companyId: number, id: number) {
	const url = ENDPOINTS.PROCESSING_COMPANY.CUSTOMTAXRELIEFGROUP.DETAIL;
	const request = API.ProcessingCompanyAPI.getCustomTaxReliefGroupById(
		companyId,
		id,
	);

	const { data, isLoading, error } = useQuery<
		{ data: CustomTaxReliefGroupDto },
		Error
	>([url, id], () => request, {
		enabled: !!id,
	});

	return { data, isLoading, error };
}

export function useCreateCustomTaxReliefGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			companyId,
			payload,
		}: {
			companyId: number;
			payload: CreateCustomTaxReliefGroupDto;
		}) =>
			API.ProcessingCompanyAPI.createCustomTaxReliefGroup(companyId, payload),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.CUSTOMTAXRELIEFGROUP.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdateCustomTaxReliefGroup() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			companyId,
			id,
		}: {
			payload: UpdateCustomTaxReliefGroupDto;
			companyId: number;
			id: number;
		}) =>
			API.ProcessingCompanyAPI.updateCustomTaxReliefGroup(
				companyId,
				id,
				payload,
			),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PROCESSING_COMPANY.CUSTOMTAXRELIEFGROUP.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeleteCustomTaxReliefGroup() {
	return useMutation(
		({ companyId, id }: { companyId: number; id: number }) =>
			API.ProcessingCompanyAPI.deleteCustomTaxReliefGroupById(companyId, id),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}